// @flow

/**
 * This file exposes two APIs for working with the URL hash string on the Category Page.
 * The URL hash is the part of the URL query string containing a '#':
 * https://oldnavy.gap.com/browse/category.do?cid=5199&nav=meganav%3AMen%3AShop%20by%20Category%3AJeans#color=1021&style=55506&storeId=3075&sortBy=low
 * The hash string encodes parameters that used to request a filtered subset of products:
 * #color=1021&style=55506&storeId=3075&sortBy=low
 *
 * `buildUrlHash` receives a object with keys for selected parameters and returns a new URL hash string
 * after processing each selected parameter with functions like commonFacetQueryBuilder and priceQueryBuilder.
 *
 * `removeInvalidHashParams` receives a URL hash string, removes invalid hash parameters, and returns a new URL hash string.
 */

const commonFacetQueryBuilder = (options) =>
  (options || []).map((option) => option.id).join(',') || undefined;

const priceQueryBuilder = (selectedPriceRange = {}) => {
  if (selectedPriceRange?.range) {
    return `${selectedPriceRange?.range.min}-${selectedPriceRange?.range.max}`;
  }

  return undefined;
};

const formatSizeGroupsForQueryString = (groupList, groupId) => {
  const formattedGroupList = groupList
    .map((groupItem) => groupItem?.id)
    .join(',');
  return `${groupId}:${formattedGroupList}`;
};

const sizeQueryBuilder = (selectedSizes) => {
  if (selectedSizes?.length > 0) {
    const groupedSizes = selectedSizes.reduce((sizes, size) => {
      const group = `${size.sfcId}-${size.dimId}`;

      return {
        ...sizes,
        [group]: [...(sizes[group] ?? []), size],
      };
    }, {});

    /*
     * pluck all the ids for groups groupedby 'sfcId-dimId'
     * to form sizes array of 'sfcId-dimId:id1,id2,id3'
     */

    return Object.keys(groupedSizes)
      .map((group) =>
        formatSizeGroupsForQueryString(groupedSizes[group], group)
      )
      .join('|');
  }

  return undefined;
};

const sizeQueryBuilderNewContract = (selectedSizes) => {
  if (selectedSizes?.length > 0) {
    const groupedSizes = selectedSizes.reduce((sizes, size) => {
      const group = size.id !== undefined ? `${size.id}` : '';

      return {
        ...sizes,
        [group]: [...(sizes[group] ?? []), size],
      };
    }, {});

    return Object.keys(groupedSizes).join(',');
  }

  return undefined;
};

const joinQueryStringSegments = (queryStringSegments: {
  [key: string]: any,
}) => {
  return Object.keys(queryStringSegments)
    .filter((key) => !!queryStringSegments[key])
    .map((key) => `${key}=${queryStringSegments[key]}`)
    .join('&');
};

/**
 * Creates hash parameter by concatenating key and value.
 */
const joinParam = ([key, value]: Array<string>): string => `${key}=${value}`;

const isValidParam = ([key]: Array<string>): boolean =>
  !!key && /^[a-zA-Z]/.test(key);

/**
 * Returns a valid hash string containing only the parameters specified in the valid hash params.
 */
export const removeInvalidHashParams = (urlHash: string): string =>
  urlHash
    .substring(1)
    .split('&')
    .map((param) => param.split('='))
    .filter(isValidParam)
    .map(joinParam)
    .join('&');

/**
 * Constructs a URL hash string to make a products request with selected params from facets, pagination, sort by, and BOPIS.
 */

type SelectedParams = { [key: string]: any };
type UrlHash = string;

const sortByAndStoreOptionsHandler = (options) => {
  const [option] = options;
  return option?.id ?? option?.searchFacetOptionId ?? undefined;
};

const buildOrderOption = (facetName, id) =>
  `${facetName + (id ? `:${id}` : '')}`;

const getFacetId = (facet) => facet.id ?? facet.searchFacetOptionId;

const mapOrderedFacets = (facet) => {
  const { facetName } = facet;
  const id = getFacetId(facet);
  return buildOrderOption(facetName, id);
};

const filterUndefined = (facet) => !!facet?.facetName;

const facetOrderQueryBuilder = (orderedFacets) =>
  (orderedFacets || []).filter(filterUndefined).map(mapOrderedFacets).join(',');

const optionsHandlers = {
  size: (options, isSwitchContractEnabled) =>
    isSwitchContractEnabled
      ? sizeQueryBuilderNewContract(options)
      : sizeQueryBuilder(options),
  price: (options) => {
    const [option] = options;
    return priceQueryBuilder(option);
  },
  storeId: (options) => sortByAndStoreOptionsHandler(options),
  sortByField: (options) => sortByAndStoreOptionsHandler(options),
  sortByDir: (options) => sortByAndStoreOptionsHandler(options),
  pageId: (options) => options,
  facetOrder: (options) => facetOrderQueryBuilder(options),
};

const adaptQueryParam = (facets, name, isSwitchContractEnabled) => {
  const options = facets[name];
  const handle = optionsHandlers[name];

  return handle
    ? handle(options, isSwitchContractEnabled)
    : commonFacetQueryBuilder(options);
};

const toQuery = (params, isSwitchContractEnabled) => {
  return (query, param) => {
    query[param] = adaptQueryParam(params, param, isSwitchContractEnabled);

    return query;
  };
};

const buildUrlHash = (
  selectedParams: SelectedParams = {},
  isSwitchContractEnabled: boolean = false
): UrlHash => {
  const queryStrings = Object.keys(selectedParams).reduce(
    toQuery(selectedParams, isSwitchContractEnabled),
    {}
  );

  return joinQueryStringSegments(queryStrings);
};

export default buildUrlHash;
